import {FC, useEffect, useRef, useState} from 'react'
import {Link, useSearchParams} from 'react-router-dom'
import {toast, ToastContainer} from 'react-toastify'
import {useIntl} from 'react-intl'
import useCancelToken from '../../../_gori/hooks/UseCancelToken'
import {toAbsoluteUrl} from '../../../_gori/helpers'
import {ROUTES} from '../../../_gori/constants'
import DownloadService from './core/_requests'

type PageState = 'loading' | 'success' | 'expired' | 'invalid'

const DownloadLayout: FC<{children: React.ReactNode}> = ({children}) => (
  <div
    className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
    style={{
      backgroundColor: '#E4EFEE',
      backgroundImage: `url(${toAbsoluteUrl('/media/gori/login/gori_bg_rocket_1.webp')})`,
      minHeight: '100vh',
    }}
  >
    <ToastContainer position='top-right' autoClose={5000} />
    <div className='container d-flex flex-center flex-column flex-column-fluid'>
      <Link to={ROUTES.LANDING.BASE} className='mb-12'>
        <img
          alt='ShipBae Logo'
          src={toAbsoluteUrl('/media/logos/big-shipbae-logo.png')}
          className='h-60px'
        />
      </Link>
      {children}
    </div>
  </div>
)

const StateCard: FC<{children: React.ReactNode}> = ({children}) => (
  <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto text-center'>
    {children}
  </div>
)

const LoadingState: FC = () => {
  const intl = useIntl()
  return (
    <StateCard>
      <div className='mb-8'>
        <span
          className='spinner-border'
          style={{width: '2.5rem', height: '2.5rem', color: '#0B6D65', borderWidth: 3}}
          role='status'
          aria-hidden='true'
        />
      </div>
      <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'DOWNLOAD_FILE_PREPARING'})}</h1>
      <div className='text-gray-400 fw-bold fs-4'>
        {intl.formatMessage({id: 'DOWNLOAD_FILE_PLEASE_WAIT'})}
      </div>
    </StateCard>
  )
}

const SuccessState: FC<{filename: string}> = ({filename}) => {
  const intl = useIntl()
  return (
    <StateCard>
      <div className='mb-8'>
        <div
          className='d-flex align-items-center justify-content-center mx-auto rounded-circle'
          style={{width: 64, height: 64, backgroundColor: '#E6F4F1'}}
        >
          <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
            <path
              d='M5 13l4 4L19 7'
              stroke='#0B6D65'
              strokeWidth='2.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>
      <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'DOWNLOAD_FILE_SUCCESS_TITLE'})}</h1>
      <div className='text-gray-400 fw-bold fs-4 mb-2'>
        {intl.formatMessage({id: 'DOWNLOAD_FILE_SUCCESS_DESC'})}
      </div>
      <div className='text-gray-600 fs-6'>{filename}</div>
    </StateCard>
  )
}
const ErrorState: FC<{
  state: 'expired' | 'invalid'
  keyParam: string
  onResend: () => void
  resending: boolean
  resendDone: boolean
}> = ({state, onResend, resending, resendDone}) => {
  const intl = useIntl()

  const content = {
    expired: {
      title: intl.formatMessage({id: 'DOWNLOAD_FILE_EXPIRED_TITLE'}),
      description: intl.formatMessage({id: 'DOWNLOAD_FILE_EXPIRED_DESC'}),
    },
    invalid: {
      title: intl.formatMessage({id: 'DOWNLOAD_FILE_INVALID_TITLE'}),
      description: intl.formatMessage({id: 'DOWNLOAD_FILE_INVALID_DESC'}),
    },
  }[state]

  return (
    <StateCard>
      {resendDone ? (
        <>
          <div className='mb-8'>
            <div
              className='d-flex align-items-center justify-content-center mx-auto rounded-circle'
              style={{width: 64, height: 64, backgroundColor: '#E6F4F1'}}
            >
              <svg width='32' height='32' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M5 13l4 4L19 7'
                  stroke='#0B6D65'
                  strokeWidth='2.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
          </div>
          <h1 className='text-dark fw-bolder mb-3'>
            {intl.formatMessage({id: 'DOWNLOAD_FILE_RESEND_TITLE'})}
          </h1>
          <div className='text-gray-600 fw-bold fs-5'>
            {intl.formatMessage({id: 'REPORT_EXPORTED_TO_MAIL'})}
          </div>
        </>
      ) : (
        <>
          <h1 className='text-dark fw-bolder mb-8'>{content.title}</h1>

          <div
            className='rounded p-5 mb-8 fs-6 text-start fw-bold'
            style={{backgroundColor: '#FBE2E1', color: '#C0382B'}}
          >
            {content.description}
          </div>

          <button
            type='button'
            className='btn btn-lg w-100 text-white fw-bold'
            style={{backgroundColor: '#0B6D65', border: 'none'}}
            onClick={onResend}
            disabled={resending}
          >
            {resending ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                />
                {intl.formatMessage({id: 'DOWNLOAD_FILE_RESENDING'})}
              </>
            ) : (
              intl.formatMessage({id: 'DOWNLOAD_FILE_RESEND_BUTTON'})
            )}
          </button>
        </>
      )}
    </StateCard>
  )
}

const DownloadFilePage: FC = () => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const {newCancelToken, isCancel} = useCancelToken()
  const hasRun = useRef(false)
  const [pageState, setPageState] = useState<PageState>('loading')
  const [keyParam, setKeyParam] = useState<string>('')
  const [resending, setResending] = useState(false)
  const [resendDone, setResendDone] = useState(false)
  const [filename, setFilename] = useState<string>('')

  useEffect(() => {
    const splashScreen = document.getElementById('splash-screen')
    if (splashScreen) splashScreen.style.display = 'none'
  }, [])

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true

    const key = searchParams.get('key')
    if (key) setKeyParam(key)

    if (!key) {
      setPageState('invalid')
      return
    }

    const initDownLoadFile = async () => {
      const config = {cancelToken: newCancelToken()}
      try {
        const result = await DownloadService.resolveKey(key, config)

        // Backend returns {status: 'not_found'} if file doesn't exist
        if (result.status === 'not_found') {
          setPageState('expired')
          return
        }

        // Fetch file and auto download
        const response = await fetch(result.download_url)
        const blob = await response.blob()

        // Auto download using anchor element
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = result.filename
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
        URL.revokeObjectURL(url)

        setFilename(result.filename)
        setPageState('success')
      } catch (error: any) {
        if (isCancel(error)) return
        // API errors are already handled by interceptor
        setPageState('expired')
      }
    }

    initDownLoadFile()
  }, [isCancel, newCancelToken, searchParams])

  const handleResend = async () => {
    if (!keyParam || resending) return
    setResending(true)
    try {
      await DownloadService.resendLink(keyParam, {cancelToken: newCancelToken()})
      setResendDone(true)
    } catch (error: any) {
      if (isCancel(error)) return

      if (error?.response?.status === 429) {
        toast.error(intl.formatMessage({id: 'DOWNLOAD_FILE_RATE_LIMIT'}))
        return
      }
    } finally {
      setResending(false)
    }
  }

  return (
    <DownloadLayout>
      {pageState === 'loading' && <LoadingState />}
      {pageState === 'success' && <SuccessState filename={filename} />}
      {(pageState === 'expired' || pageState === 'invalid') && (
        <ErrorState
          state={pageState}
          keyParam={keyParam}
          onResend={handleResend}
          resending={resending}
          resendDone={resendDone}
        />
      )}
    </DownloadLayout>
  )
}

export {DownloadFilePage}
